import * as _ from 'lodash';
import { Utils } from "../Utils/Utils";
import { StringUtils } from "../Utils/StringUtils";
import { ArgValidator} from '../Utils/ArgValidator';
import { ArrayUtils } from '../Utils/ArrayUtils';
import {WidgetApiBase} from './WidgetBase';
import {Dialog} from './Dialog';

export class DatatableApi extends WidgetApiBase {
    private _dataTable: DataTables.DataTable;

    constructor(id: string) {
        super(id);
    }

    protected getInitOptions(): DataTables.Settings {
        return null;
    }

    protected getDatatable(doInit: boolean = false): DataTables.DataTable {
        if (!this._dataTable) {
            var opt = doInit ? this.getInitOptions() : undefined;
            this._dataTable = this.jq.DataTable(opt);
        }
        return this._dataTable;
    }

    public getElement(selector: any): JQuery {
        return $(selector, this.jq);
    }

    public columns(columnIndex: number): DataTables.ColumnsMethods {
        return this.getDatatable().columns(columnIndex);
    }

    public search(value: string): void {
        this.getDatatable().search(value);
    }

    public row(rowSelector: any): DataTables.RowMethods {
        var row = this.getDatatable().row(rowSelector);
        return row.length > 0 ? row : null;
    }

    public rowDataId(row: DataTables.RowMethods | JQuery): any {
        var r = row as any;
        var tr: JQuery;
        if (r instanceof $.fn.DataTable.Api) {
            tr = Utils.getJQueryById(r.id());
        } else if (r instanceof jQuery) {
            tr = row as JQuery;
        } else {
            throw new Error(`Row type ${typeof row} is not supported!`);
        }

        return tr.attr('data-id');
    }

    public rowById(rowId: string): DataTables.RowMethods {
        return this.row(`[data-id="${rowId}"]`);
    }

    public draw(reset?: boolean): void {
        this.getDatatable().draw(reset);
    }

    public static attach(id: string): void {
        var elem = Utils.getJQueryById(id);
        elem.DataTable();
    }
}

export type DatatableHasDetailFunction = (rowData: any, row?: DataTables.RowMethods) => boolean;
export type DatatableHasDetailType = DatatableHasDetailFunction | boolean;

/**
 * @interface DatatableOptions - options data for Datatable.
 */
export interface DatatableOptions {
    /**
     * @property url - url to server side for JSON data.
     */
    url: string;

    /**
     * @property dom - datatables dom 
     */
    dom: string;

    scrollX?: boolean;

    /**
     * @property rowId - name of rowId field in data source.
     */
    rowId: string;

    /**
     * @property processing - show/hide processing panel while requesting data from server.
     * @default default value is true
     */
    processing?: boolean;

    /**
     * @property serverSide - true to request data from server, false to do client side data operations.
     * @default default value is true
     */
    serverSide?: boolean;

    /**
     * @property autoWidth - enable or disable automatic column width calculation.
     * @default default value is true
     */
    autoWidth?: boolean;

    pageLength?: number;

    lengthMenu?: (number | string)[] | (number | string)[][];

    /**
     * @property columns - list of columns
     */
    columns: Array<DataTables.ColumnSettings>;

    /**
     * @property order - array of initial order
     */
    order?: (string | number)[] | (string | number)[][];

    ordering?: boolean;

    /**
     * @property autoFocusSearch - true to auto focus search edit (if exist)
     */
    autoFocusSearch?: boolean;

    paging?: boolean;

    errorHandler?: (xhr: JQueryXHR, textStatus: string, error: string) => void;

    errorElementId?: string;

    fixedHeader?: boolean | DataTables.FixedHeaderSettings;

    /**
     * @property callbacks - client callbacks for data table events
     */
    callbacks?: {
        /**
        * Callback for whenever a TR element is created for the table's body.
        */
        createdRow?: DataTables.FunctionCreateRow;

        /**
        * Pre-draw callback.
        */
        preDrawCallback?: (datatable: DatatableApi) => boolean;

        /**
         * Function that is called every time DataTables performs a draw.
         */
        drawCallback?: (datatable: DatatableApi) => void;
    };

    /**
     * @property rowDetail - information about row's child detail support
     */
    rowDetail?: {
        /**
         * @property toggleSelector - jquery selector to attach click event to toggle visibility of child detail
         * @defaultvalue 'tr td'
         */
        toggleSelector?: string;

        /**
         * @method hasDetail - true means row will display child detail, false to not show.
         */
        hasDetail: DatatableHasDetailType;

        /**
         * @method render - renders child detail for specified row
         */
        render: (rowData: any, row?: DataTables.RowMethods) => string;    
    }
}

var defaultOptions: DatatableOptions = {
    url: null,
    dom: null,
    rowId: null,
    columns: null,
    autoFocusSearch: true
};

export class Datatable extends DatatableApi {
    private _detailRowsIds: Array<string>;
    private _options: DatatableOptions;
    private _searchEdit: JQuery;

    constructor(id: string, options: DatatableOptions) {
        super(id);
        ArgValidator.ensureIsNotNullOrUndefined(options, 'options');
        ArgValidator.ensureStringIsNotNullOrEmpty(options.url, 'options.url');
        ArgValidator.ensureIsNotNullOrUndefined(options.columns, 'options.columns');

		//this._options =  options;
		this._options = _.assign({}, defaultOptions, options) as any;
		this.internalCreatedRow = this.internalCreatedRow.bind(this);
		this.internalDrawCallback = this.internalDrawCallback.bind(this);
		this.toggleDetailRow = this.toggleDetailRow.bind(this);
		this.internalInitComplete = this.internalInitComplete.bind(this);

		this.getDatatable(true);

		var rowDetail = this._options.rowDetail;
		if (rowDetail) {
			// initialize detail rows
			this._detailRowsIds = [];

			this.getElement('tbody').on('click', rowDetail.toggleSelector, this.toggleDetailRow);
		}
	}
	
    private toggleDetailRow(e: JQueryEventObject): void {
        var tr = $(e.target).closest('tr');
        var row = this.row(tr);
        this.showDetailRow(row, true);

        (<any>this.getDatatable()).fixedHeader.adjust();
    }

    private showDetailRow(row: DataTables.RowMethods, toggle: boolean): void {
        if (!row) {
            return;
        }

        var tr = $(row.node());
        var rowData = row.data();
        var hasDetail = false;
        var rowDetail = this._options.rowDetail;
        if (rowDetail) {
            if (typeof rowDetail.hasDetail === 'function') {
                hasDetail = (rowDetail.hasDetail as DatatableHasDetailFunction)(rowData, row);
            } else if (typeof rowDetail.hasDetail === 'boolean') {
                hasDetail = <boolean>rowDetail.hasDetail;
            }
        }

        if (!hasDetail) {
            return;
        }

        var rowId = this.rowDataId(row);
        var idx = ArrayUtils.findIndex(this._detailRowsIds, rowId);

        if (toggle && row.child.isShown()) {
            tr.removeClass('details bg-blue bg-font-blue');
            row.child.hide();

            // Remove from the 'open' array
            this._detailRowsIds.splice(idx, 1);
        } else {
            tr.addClass('details bg-blue bg-font-blue');
            var detailRow = rowDetail.render(rowData, row);
            row.child(detailRow).show();

            // Add to the 'open' array
            if (idx === -1) {
                this._detailRowsIds.push(rowId);
            }                        
        }
    }


    private internalCreatedRow(row: Node, data: any[] | Object, dataIndex: number): void {
        var $tr = $(row);
        $tr.attr('data-id', data[this._options.rowId]);

        var createdRow = this._options.callbacks ? this._options.callbacks.createdRow : null;
        if (createdRow) {
            createdRow(row, data, dataIndex);
        }
    }

    private internalDrawCallback(settings: DataTables.SettingsLegacy): void {
        if (Utils.isNullOrUndefined(this._options.rowDetail)) {
            return;
        }

        _.each(this._detailRowsIds, (rowId: string) => {
            var row = this.rowById(rowId);
            if (row) {
                this.showDetailRow(row, false);
            }
        });

        var drawCallback = this._options.callbacks ? this._options.callbacks.drawCallback : null;
        if (drawCallback) {
            return drawCallback(this);
        }
    }

    private internalInitComplete(settings: DataTables.SettingsLegacy): void {
        this._searchEdit = $('div.dataTables_filter input', settings.nTableWrapper);
        if (this._options.autoFocusSearch) {
            Utils.focusControl(this._searchEdit);
        }
    }

    protected getInitOptions(): DataTables.Settings {
        var self = this;
        var o = this._options;
        o.processing = Utils.valueOrDefault(o.processing, true);
        o.serverSide = Utils.valueOrDefault(o.serverSide, true);
        o.autoWidth = Utils.valueOrDefault(o.autoWidth, true);
        o.pageLength = Utils.valueOrDefault(o.pageLength, 10);

        if (o.rowDetail) {
            o.rowDetail.toggleSelector = Utils.valueOrDefault(o.rowDetail.toggleSelector, 'tr td');
        }

        function removeProcessingPanel(): void {
	        var id = self._id + '_processing';
	        var divProcessing = $(`#${id}`)
	        if (!divProcessing) {
		        return;
            }
            divProcessing.css("display", "none");
	        //divProcessing.parentElement.removeChild(divProcessing);
		}

        var result: DataTables.Settings = {
            ajax: {
                url: o.url,
                method: "POST",
                beforeSend: () => {},
				error: (xhr, textStatus, error) => {
					removeProcessingPanel();
					if (Utils.isNullOrUndefined(o.errorHandler)) {
                        if (xhr.status === 404 || xhr.status === 401) {
                            window.location.reload();
                        } else {
                            var parent = this.jq.parent() as JQuery;
                            if (!StringUtils.isNullOrEmpty(xhr.responseText)) {
                                let parseResult = Utils.parseJson(xhr.responseText);
                                if (parseResult.isValid && parseResult.json &&
                                    !StringUtils.isNullOrEmpty(parseResult.json['Message'])) {
                                    setTimeout(() => {
                                        let msg = parseResult.json['Message'];
                                        Dialog.error(msg);
                                    }, 0);
                                    return;
                                }
                            }

                            var style = `display: block; margin-top: 15px; padding-top: 7px; background-color: white; color: red; z-index: 1000;` +
                                `position: relative;left: auto;margin-left: 0; width: 500px; font-size: 11pt; height: auto;`;
                            var errDiv = $(`<div class="dataTables_processing panel panel-default" style="${style}">` +
                                `Could not load data!<br/>${error} (${xhr.status})` +
                                `</div>`);
                            parent.append(errDiv);
                        }
                    }
                    else {
                        o.errorHandler(xhr, textStatus, error);
                    }
                }
            },
            rowId: (data: any) => (`${this._id}_row_${data[o.rowId]}`),
            dom: o.dom,
            lengthChange: true,
            lengthMenu: Utils.valueOrDefault(o.lengthMenu, [10, 20, 50, 100]),
            orderCellsTop: true,
            processing: o.processing,
            serverSide: o.serverSide,
            autoWidth: o.autoWidth,
            pageLength: o.pageLength,
            columns: o.columns,
            order: o.order,
            ordering: o.ordering,
            paging: o.paging,
            fixedHeader: o.fixedHeader,
            scrollX: o.scrollX,

            createdRow: this.internalCreatedRow,
            drawCallback: this.internalDrawCallback,
            initComplete: this.internalInitComplete
        };

        if (o.callbacks) {
            if (o.callbacks.preDrawCallback) {
                result.preDrawCallback = (settings: DataTables.SettingsLegacy) => {
                    return o.callbacks.preDrawCallback(self);
                };
            }
            if (o.callbacks.drawCallback) {
                result.drawCallback = (settings: DataTables.SettingsLegacy) => {
                    return o.callbacks.drawCallback(self);
                };
            }
        }

        return result;
    }
}


/** WEBPACK FOOTER **
 ** ./Typescript/Widgets/Datatable.ts
 **/